import * as React from "react";

import { cn } from "../../lib/utils";

/**
 * Fixed drawing box. Callers scale with `size`; keeping the geometry constant
 * means `strokeWidth` reads the same at 22 px and at 52 px.
 */
const VIEWBOX = 36;
const DEFAULT_SIZE = 36;
const DEFAULT_STROKE_WIDTH = 4;
/** Smallest ring that still reads as a ring rather than a filled blob. */
const MIN_RADIUS = 2;

export interface ProgressDonutProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "children"
> {
  /** 0-100. Values outside the range are clamped; NaN renders as 0. */
  value: number;
  /** Rendered width and height in px. */
  size?: number;
  /** Ring thickness in viewBox units. Clamped to leave a drawable radius. */
  strokeWidth?: number;
  /**
   * Accessible name, e.g. "Onboarding-förlopp".
   *
   * Omit it when the same number is already written next to the ring — the
   * donut then renders decoratively rather than as an unnamed progressbar,
   * which a screen reader would announce as a value with nothing to attach it
   * to. Percentage text passed as `children` cannot supply the name: a
   * progressbar's descendants are presentational.
   */
  label?: string;
  trackClassName?: string;
  indicatorClassName?: string;
  /** Centred content, typically the percentage in mono. */
  children?: React.ReactNode;
}

/**
 * A percentage ring. Deliberately dependency-free — Recharts renders the
 * dashboard's data donuts, but this one is chrome: it sits in a floating widget
 * and a nav pill where a chart runtime would be dead weight.
 */
const ProgressDonut = React.forwardRef<HTMLSpanElement, ProgressDonutProps>(
  (
    {
      value,
      size = DEFAULT_SIZE,
      strokeWidth = DEFAULT_STROKE_WIDTH,
      label,
      className,
      trackClassName,
      indicatorClassName,
      children,
      ...props
    },
    ref,
  ) => {
    const clamped = Number.isFinite(value)
      ? Math.min(100, Math.max(0, value))
      : 0;
    // Guard the SVG geometry: a stroke at or past the viewBox leaves a zero or
    // negative radius, and a non-finite size renders NaN attributes. Clamp
    // rather than throw — a broken ring should never take the page down.
    const safeSize = Number.isFinite(size) && size > 0 ? size : DEFAULT_SIZE;
    const safeStroke =
      Number.isFinite(strokeWidth) && strokeWidth > 0
        ? Math.min(strokeWidth, VIEWBOX - MIN_RADIUS * 2)
        : DEFAULT_STROKE_WIDTH;
    const radius = (VIEWBOX - safeStroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const filled = (clamped / 100) * circumference;

    // An unnamed progressbar is worse than no role at all, so the semantics
    // follow the name: labelled rings are progressbars, unlabelled ones are
    // decoration beside the number they illustrate.
    const semantics = label
      ? ({
          role: "progressbar",
          "aria-valuenow": Math.round(clamped),
          "aria-valuemin": 0,
          "aria-valuemax": 100,
          "aria-label": label,
        } as const)
      : ({ role: "presentation" } as const);

    return (
      <span
        ref={ref}
        {...semantics}
        className={cn(
          "relative inline-flex shrink-0 items-center justify-center",
          className,
        )}
        style={{ width: safeSize, height: safeSize }}
        {...props}
      >
        <svg
          viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
          width={safeSize}
          height={safeSize}
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <circle
            cx={VIEWBOX / 2}
            cy={VIEWBOX / 2}
            r={radius}
            strokeWidth={safeStroke}
            className={cn("stroke-brand-sand-deep", trackClassName)}
          />
          {clamped > 0 ? (
            <circle
              cx={VIEWBOX / 2}
              cy={VIEWBOX / 2}
              r={radius}
              strokeWidth={safeStroke}
              strokeLinecap="round"
              strokeDasharray={`${filled} ${circumference}`}
              transform={`rotate(-90 ${VIEWBOX / 2} ${VIEWBOX / 2})`}
              className={cn(
                "stroke-brand-coral transition-[stroke-dasharray] duration-300 ease-out motion-reduce:transition-none",
                indicatorClassName,
              )}
            />
          ) : null}
        </svg>
        {/* Nullish, not truthy: a bare `{0}` is exactly what the guide renders
            before anyone has done anything, and truthiness would drop it. */}
        {children != null ? (
          <span className="absolute inset-0 flex items-center justify-center">
            {children}
          </span>
        ) : null}
      </span>
    );
  },
);
ProgressDonut.displayName = "ProgressDonut";

export { ProgressDonut };
